import type {
  InventoryPage,
  WarehouseList,
} from '../api/active/generated/inventory';
import type { ActiveTransport } from '../api/active/transport';

export class InventoryApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

export type InventoryReadStatus = 'KNOWN' | 'UNKNOWN';
export type InventoryWarehouseCode =
  | '0053'
  | '013'
  | '0075'
  | '0064'
  | '0063'
  | '0065';

export type InventoryListQuery = {
  page: number;
  pageSize: number;
  deviceId?: readonly string[];
  deviceIds?: readonly string[];
  search?: string;
  warehouseCode?: InventoryWarehouseCode;
  readStatus?: InventoryReadStatus;
  displayQuantityMin?: number;
  displayQuantityMax?: number;
};

async function apiError(result: Response): Promise<InventoryApiError> {
  let problem: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
  } = {};
  try {
    problem = (await result.json()) as typeof problem;
  } catch {
    // Preserve the HTTP status if a proxy returned no Problem body.
  }
  return new InventoryApiError(
    result.status,
    problem.code ?? problem.errorCode,
    problem.detail ??
      problem.title ??
      `库存请求失败（HTTP ${result.status}）。`,
  );
}

function inventoryQueryString(query: InventoryListQuery) {
  const parameters = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  for (const value of query.deviceId ?? [])
    parameters.append('deviceId', value);
  for (const value of query.deviceIds ?? [])
    parameters.append('deviceIds', value);
  if (query.search) parameters.set('search', query.search);
  if (query.warehouseCode) parameters.set('warehouseCode', query.warehouseCode);
  if (query.readStatus) parameters.set('readStatus', query.readStatus);
  if (query.displayQuantityMin !== undefined)
    parameters.set('displayQuantityMin', String(query.displayQuantityMin));
  if (query.displayQuantityMax !== undefined)
    parameters.set('displayQuantityMax', String(query.displayQuantityMax));
  return parameters.toString();
}

async function response<T>(
  transport: ActiveTransport,
  path: string,
  signal?: AbortSignal,
) {
  const result = await transport.requestResponse(path, { signal });
  if (!result.ok) throw await apiError(result);
  return (await result.json()) as T;
}

export function createInventoryApi(transport: ActiveTransport) {
  return {
    listWarehouses: (signal?: AbortSignal) =>
      response<WarehouseList>(transport, '/warehouses', signal),
    listInventory: (query: InventoryListQuery, signal?: AbortSignal) =>
      response<InventoryPage>(
        transport,
        `/inventory?${inventoryQueryString(query)}`,
        signal,
      ),
  };
}

export type InventoryReadPort = ReturnType<typeof createInventoryApi>;
